"use client";

import { useCallback, useState, useSyncExternalStore } from "react";
import { useTranslations } from "next-intl";
import { formatRegionalTime } from "@/lib/i18n/config/languages";
import { cn } from "@/lib/utils";
import { useTranslation } from "@/hooks/use-translation";
import {
  clearMissingKeys,
  getMissingKeys,
  isHighlightMissingActive,
  subscribeI18nStore,
  toggleHighlightMissing,
  type MissingKeyEntry,
} from "../stores/i18n-store";

function buildCopyJson(keys: MissingKeyEntry[]): string {
  // Structure: { [lng]: { [ns]: string[] } }
  const grouped: Record<string, Record<string, string[]>> = {};
  for (const entry of keys) {
    const lngGroup: Record<string, string[]> = (grouped[entry.lng] ??= {});
    const nsList: string[] = (lngGroup[entry.ns] ??= []);
    nsList.push(entry.key);
  }
  return JSON.stringify(grouped, null, 2);
}

export function I18nPanel() {
  const t = useTranslations("debug.i18nPanel");
  const { formatLocale, timeZone } = useTranslation("common");
  const keys = useSyncExternalStore(
    subscribeI18nStore,
    getMissingKeys,
    getMissingKeys,
  );
  const isHighlightActive = useSyncExternalStore(
    subscribeI18nStore,
    isHighlightMissingActive,
    isHighlightMissingActive,
  );
  const [copied, setCopied] = useState(false);

  const handleClear = useCallback(() => {
    clearMissingKeys();
  }, []);

  const handleCopyJson = useCallback(() => {
    void navigator.clipboard
      .writeText(buildCopyJson(getMissingKeys()))
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      });
  }, []);

  const handleToggleHighlight = useCallback(() => {
    toggleHighlightMissing();
  }, []);

  return (
    <div className="space-y-2">
      {/* Top Banner / Actions Bar */}
      <div className="flex flex-wrap items-center justify-between gap-2 pb-1 border-b border-border/40">
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground">
            {t("missingCount", { count: keys.length })}
          </span>
          {isHighlightActive && (
            <span className="rounded border border-red-500/20 bg-red-500/10 px-1.5 py-0.5 font-mono text-sm font-semibold text-red-500">
              {t("highlightActive")}
            </span>
          )}
        </div>

        <div className="flex items-center gap-1.5">
          <button
            type="button"
            onClick={handleToggleHighlight}
            className={cn(
              "text-sm px-2.5 py-1 rounded-md border font-mono font-medium transition-all cursor-pointer",
              isHighlightActive
                ? "bg-red-500 text-white border-red-600 font-bold shadow-xs"
                : "bg-muted/50 border-border text-muted-foreground hover:text-foreground hover:bg-muted",
            )}
            title={t("highlightTitle")}
          >
            {isHighlightActive ? t("disableHighlight") : t("enableHighlight")}
          </button>

          {keys.length > 0 && (
            <>
              <button
                type="button"
                onClick={handleCopyJson}
                className="cursor-pointer rounded border border-border bg-muted/50 px-2 py-1 text-sm text-muted-foreground transition-colors hover:text-foreground"
              >
                {copied ? t("copied") : t("copyJson")}
              </button>
              <button
                type="button"
                onClick={handleClear}
                className="cursor-pointer rounded border border-border bg-muted/50 px-2 py-1 text-sm text-muted-foreground transition-colors hover:text-foreground"
              >
                {t("clear")}
              </button>
            </>
          )}
        </div>
      </div>

      {keys.length === 0 ? (
        <div className="text-sm text-muted-foreground py-6 text-center">
          {t("empty")}
        </div>
      ) : (
        <div className="space-y-1 max-h-[50vh] overflow-y-auto pr-1 scrollbar-thin">
          {keys.map((entry) => (
            <div
              key={`${entry.lng}:${entry.ns}:${entry.key}`}
              className="group flex items-start gap-2 rounded-lg border border-border/40 px-2.5 py-1.5 font-mono text-sm transition-colors hover:bg-muted/30"
            >
              <span className="shrink-0 px-1.5 py-0.5 rounded bg-red-500/10 text-red-500 font-bold font-sans">
                {entry.count}
                {"×"}
              </span>
              <div className="flex-1 min-w-0">
                <div className="text-foreground font-semibold break-all">
                  {entry.key}
                </div>
                <div className="mt-0.5 text-sm text-muted-foreground/60">
                  {t("language")}{" "}
                  <span className="text-foreground">{entry.lng}</span> {"·"}{" "}
                  {t("namespace")}{" "}
                  <span className="text-foreground">{entry.ns}</span>
                </div>
              </div>
              <span className="shrink-0 text-sm text-muted-foreground/40">
                {formatRegionalTime(entry.firstSeen, formatLocale, timeZone)}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
